/** Straight stock-race divider panel for the loading-yard handling lane. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'race-panel';

  const brightSteel = surface('metal', 0x9aa2a8);
  const dullSteel = surface('metal', 0x767d82);

  // With posts at +/-0.94 and length 2.00, 2.00 - 0.94 - 0.94 = 0.12 m; it
  // reads as the normal post rhythm of a stock race, and is a fix not a
  // compromise. The panel must meet the authored two-metre lane extent rather
  // than letting nominal post centres quietly shorten the blocking silhouette.
  for (const [x, z, name] of [
    [-0.005, -0.94, 'rear-dull-post'],
    [0.005, 0.94, 'front-dull-post'],
  ] as const) {
    const post = part(new THREE.BoxGeometry(0.07, 1.15, 0.07), dullSteel, name);
    post.position.set(x, 0.575, z);
    group.add(post);
  }

  // Eight radial segments are deliberate poly budget: these rails only need to
  // read as cold repeated stock-race steel under rain, not as round pipe at close range.
  for (const [y, name] of [
    [0.20, 'bright-rail-lowest'],
    [0.42, 'bright-rail-low'],
    [0.63, 'bright-rail-middle'],
    [0.85, 'bright-rail-high'],
    [1.06, 'bright-rail-highest'],
  ] as const) {
    const rail = part(new THREE.CylinderGeometry(0.0225, 0.0225, 2, 8), brightSteel, name);
    rail.rotation.x = Math.PI / 2;
    rail.position.set(0, y, 0);
    group.add(rail);
  }

  // A real stock-race animal kick-board is solid at the bottom. At 0.30 m it is
  // almost one 0.32 m Rosalie hiding height (D-012): that is an observation for
  // the room author, not a gameplay claim made by this passive kit asset.
  const kickboard = part(new THREE.BoxGeometry(0.02, 0.30, 2), dullSteel, 'dull-steel-kickboard');
  kickboard.position.set(0, 0.15, 0);
  group.add(kickboard);

  return group;
}

export const racePanelAsset: AssetDefinition = {
  id: 'race-panel',
  label: 'Stock-race panel',
  description: 'Two-metre galvanized stock-race divider with repeated rails and kick-board.',
  footprint: { width: 0.08, height: 1.15, length: 2.00 },
  create: build,
};
